import React, {Fragment} from "react";
import {Button, Card, CardBody, Col} from "reactstrap";

// "Sales channels" card: the "Publish to all channels" flag plus one checkbox per Shopify sales channel.
// The per-channel list is only available once the product exists on Shopify (the channel endpoint has
// nothing to read before that). While "Publish to all channels" is ticked every channel is shown ticked
// and locked, because the backend publishes everywhere and ignores the selection.
const checkbox = {width: 18, height: 18, margin: 0, flexShrink: 0, cursor: 'pointer'};

const SalesChannels = (props) => {
    const {register, watch, isPublished, channelPublicationsState, selectedPublications, setSelectedPublications, onRetry,
        indiaShop, isThirdPartySeller} = props;
    const publishToAll = !!watch('publish_to_all');
    const publishToIndia = !!watch('publish_to_india');
    const indiaListing = indiaShop?.listing;
    const publications = channelPublicationsState?.data?.publications || [];
    const selectedGids = (selectedPublications || []).map(item => item.gid);

    const toggleChannel = (publication) => {
        if (publishToAll) return;
        if (selectedGids.includes(publication.gid)) {
            setSelectedPublications((selectedPublications || []).filter(item => item.gid !== publication.gid));
        } else {
            setSelectedPublications([...(selectedPublications || []), publication]);
        }
    };

    const {ref: publishToAllRef, ...publishToAllField} = register('publish_to_all');
    const {ref: publishToIndiaRef, ...publishToIndiaField} = register('publish_to_india');

    // India Shop is the Goddiva India marketplace, not a Shopify publication of this store, so
    // "Publish to all channels" never ticks or locks it. Only primary-seller (Goddiva) products
    // can go to India, so it is hidden for third-party sellers.
    let indiaHelp;
    if (indiaShop && !indiaShop.sync_enabled) {
        indiaHelp = 'India sync is currently disabled (IN region off), so nothing will be sent to India.';
    } else if (indiaListing && indiaListing.sync_status === 'synced') {
        indiaHelp = `Already in the India marketplace${indiaListing.status ? ` (${indiaListing.status})` : ''}. It will not be created again.`;
    } else if (publishToIndia) {
        indiaHelp = 'On Save & Publish this product is created in the Goddiva India marketplace as a Draft.';
    } else {
        indiaHelp = 'Not sent to the Goddiva India marketplace.';
    }

    let help;
    if (publishToAll) {
        help = 'On Save & Publish this product is published to every Shopify sales channel.';
    } else if (!isPublished) {
        help = 'The product will be created on Shopify but will not be visible on the storefront or any sales channel.';
    } else if (selectedGids.length > 0) {
        help = `Only the ${selectedGids.length} channel(s) ticked below are used. The product is removed from the others.`;
    } else {
        help = 'No channel is ticked below. The product will be removed from every sales channel and will not appear on the storefront.';
    }

    return (
        <Fragment>
            <Card>
                <CardBody>
                    <div className="ribbon ribbon-clip ribbon-info ribbon-left mb-5">Sales channels</div>
                    <div className="form-row" style={{marginTop: 24}}>
                        <Col md="12">
                            <label htmlFor="publish_to_all" className="d-flex align-items-center mb-1" style={{gap: 10, cursor: 'pointer'}}>
                                <input id="publish_to_all" type="checkbox" style={checkbox} ref={publishToAllRef} {...publishToAllField}/>
                                <span className="text-dark" style={{fontSize: 14, fontWeight: 600}}>Publish to all channels</span>
                            </label>
                            <div className={publishToAll ? 'text-muted' : ''} style={{fontSize: 13, marginLeft: 28, color: publishToAll ? undefined : '#9a3412', fontWeight: publishToAll ? 400 : 500}}>
                                {help}
                            </div>

                            {isPublished &&
                                <div className="mt-4">
                                    <div className="text-muted mb-2" style={{fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em'}}>
                                        Channels
                                    </div>

                                    {channelPublicationsState?.loading &&
                                        <div className="text-muted" style={{fontSize: 13}}>Loading sales channels…</div>}

                                    {!channelPublicationsState?.loading && channelPublicationsState?.error &&
                                        <div className="text-danger" style={{fontSize: 13}}>
                                            Failed to load sales channels.{' '}
                                            <Button color="link" type="button" className="p-0 align-baseline" style={{fontSize: 13}} onClick={onRetry}>
                                                Retry
                                            </Button>
                                        </div>}

                                    {!channelPublicationsState?.loading && !channelPublicationsState?.error &&
                                        channelPublicationsState?.data && publications.length === 0 &&
                                        <div className="text-muted" style={{fontSize: 13}}>No sales channels found for this product.</div>}

                                    {!channelPublicationsState?.loading && !channelPublicationsState?.error && publications.length > 0 &&
                                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', rowGap: 14, columnGap: 16}}>
                                            {publications.map((publication) => {
                                                const checked = publishToAll || selectedGids.includes(publication.gid);
                                                return (
                                                    <label key={publication.gid} className="d-flex align-items-center mb-0"
                                                           style={{gap: 8, minWidth: 0, cursor: publishToAll ? 'default' : 'pointer', opacity: publishToAll ? 0.55 : 1}}>
                                                        <input type="checkbox" style={{...checkbox, width: 16, height: 16}} checked={checked} disabled={publishToAll}
                                                               onChange={() => toggleChannel(publication)}/>
                                                        <span className="text-dark" style={{fontSize: 14}}>{publication.name}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>}
                                </div>}

                            {!isThirdPartySeller &&
                            <div className="mt-4 pt-3" style={{borderTop: '1px solid #eee'}}>
                                <div className="text-muted mb-2" style={{fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em'}}>
                                    Other marketplaces
                                </div>
                                <label htmlFor="publish_to_india" className="d-flex align-items-center mb-1" style={{gap: 8, cursor: 'pointer'}}>
                                    <input id="publish_to_india" type="checkbox" style={{...checkbox, width: 16, height: 16}}
                                           ref={publishToIndiaRef} {...publishToIndiaField}/>
                                    <span className="text-dark" style={{fontSize: 14}}>India Shop</span>
                                </label>
                                <div className="text-muted" style={{fontSize: 13, marginLeft: 24}}>
                                    {indiaHelp}
                                    {indiaListing && indiaListing.sync_status === 'failed' &&
                                        <div className="text-danger">Last India push failed: {indiaListing.sync_error}</div>}
                                </div>
                            </div>}
                        </Col>
                    </div>
                </CardBody>
            </Card>
        </Fragment>
    );
}

export default SalesChannels;
